/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { switchMap, tap } from 'rxjs/operators';

import { FormHelpersService, InviteService, ModalService, SignupService, UtilsService } from 'app/shared/services';
import { Constants } from 'app/shared/models/constants.model';
import { UserData } from '@shared/models/user.model';
import { InviteDTO } from '@shared/models/invite.model';
import { AthleteInvitation } from '@shared/models/menu.model';

@Component({
  selector: '[addAthleteEventModal]',
  templateUrl: './add-athlete-event-modal.component.html',
  styleUrls: ['./add-athlete-event-modal.component.scss'],
})
export class AddAthleteEventModalComponent implements OnInit {
  @Input() currentUser: UserData;
  @Input() purchase: any;

  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  processing = false;
  form: FormGroup;
  errorMessage: string;
  purchaseTypeName: string;
  existingAthletes: any[];
  showAddAthlete = false;
  logo: string;

  constructor(
    public fhs: FormHelpersService,
    private modalService: ModalService,
    private inviteService: InviteService,
    private signUpService: SignupService,
    private utils: UtilsService
  ) {}

  ngOnInit() {
    this.form = new FormGroup({
      purchaseId: new FormControl('', Validators.required),
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      gradYear: new FormControl(''),
      teamName: new FormControl(''),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern('^[a-zA-Z0-9._+-]{1,}@{1}[a-zA-Z0-9.-]{1,}[.]{1}[a-zA-Z+]{2,}'),
        this.currentUserEmail,
      ]),
    });
    this.form.get('purchaseId').setValue(this.purchase.id);
    this.purchaseTypeName = Constants.PURCHASE_TYPE_NAME[this.purchase.type];
    this.logo = this.purchase?.event?.logo?.url
      ? this.purchase?.event?.logo?.url
      : '/assets/images/rebrand/nextpro-logo.png';
    if (this.purchase.type === Constants.PURCHASE_TYPE.HR) {
      this.logo = 'assets/images/svg/hr-select.svg';
    } else if (this.purchase.type === Constants.PURCHASE_TYPE.PRO_HR) {
      this.logo = 'assets/images/svg/hr-pro.svg';
    } else if (this.purchase.type === Constants.PURCHASE_TYPE.PRO_FEEDBACK) {
      const reviewerSlug = this.utils.getSlugForName(this.purchase.metadata.reviewer);
      this.logo = `assets/images/pro-feedback/${reviewerSlug}-thumb.png`;
    }

    this.getExistingInvites(this.currentUser);
  }

  addClick() {
    if (this.form.invalid) {
      this.errorMessage = 'Please fill out all of the athlete info below';
      return;
    }

    this.processing = true;
    const formValues = this.form.value;

    this.signUpService
      .prepareInviteeAccount(formValues, this.purchase.type, true)
      .pipe(
        switchMap((result: any) => {
          const teamPackageofParent =
            this.purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_TP &&
            this.currentUser.type === Constants.USER_TYPE.PARENT;
          // create new invite payload
          const payload = {
            inviteeEmail: formValues.email,
            user: { id: this.currentUser.id },
            purchase: { id: formValues.purchaseId },
            toUser: null,
            isTeamInvite: teamPackageofParent,
            isTPAthlete: teamPackageofParent && this.purchase.userId === this.currentUser.id,
          };

          // set user ID if account already exists
          // for this email
          if (result && result.data && result.data.id) {
            payload.toUser = { id: result.data.id };
          }

          return this.inviteService.addInvite(payload);
        }),
        tap((newInvite: any) => {
          // don't send invite email for HR and PRO HR
          if (
            this.purchase.type !== Constants.PURCHASE_TYPE.HR &&
            this.purchase.type !== Constants.PURCHASE_TYPE.PRO_HR
          ) {
            this.inviteService.sendInviteEmail(formValues.email, newInvite.id).subscribe();
          }
        })
      )
      .subscribe(() => {
        this.close(true);
      });
  }

  close(shouldRefresh = false) {
    this.modalService.hideModal();
    this.closeModal.emit(shouldRefresh);
  }

  setAthlete(selectedAthlete) {
    const spaceIndex = selectedAthlete.name.indexOf(' ');
    const firstName = selectedAthlete.name.slice(0, spaceIndex);
    const lastName = selectedAthlete.name.slice(spaceIndex + 1);
    this.form.patchValue({
      firstName,
      lastName,
      gradYear: selectedAthlete.invite.purchase.inviteeGradYear,
      teamName: selectedAthlete.invite.purchase.inviteeTeamName,
      email: selectedAthlete.email,
    });
  }

  showNewAthleteForm($event) {
    $event.preventDefault();
    this.showAddAthlete = true;
    this.form.reset();
    this.form.get('purchaseId').setValue(this.purchase.id);
  }

  private currentUserEmail = (control: FormControl) => {
    if (this.currentUser && control.value === this.currentUser.email) {
      return { currentUserEmail: true };
    }
    return null;
  };

  private getExistingInvites(user: UserData): any[] {
    // if not logged in
    if (!user || !user.id) {
      return [];
    }

    this.inviteService.checkInvitesByUserId(user.id).subscribe((invites: InviteDTO[]) => {
      const athletes = invites
        .filter((invite) => !invite.isTeamInvite && invite.userId === user.id)
        .filter(
          (invite) =>
            !!invite.purchase &&
            invite.inviteeEmail &&
            (!invite.toUser || (invite.toUser && invite.toUser.id !== user.id))
        )
        .reduce((list, invite: InviteDTO) => {
          // if athlete with same email is already in list then don't add to list.
          if (
            list.some((existingAthlete) => {
              return (
                existingAthlete.email === invite.inviteeEmail ||
                (invite.toUser && existingAthlete.email === invite.toUser.email)
              );
            })
          ) {
            return list;
          }

          let athlete: AthleteInvitation = {
            isInvitation: true,
            name: invite.purchase.inviteeFullName || invite.inviteeEmail,
            email: invite.inviteeEmail,
            status: 'pending',
            icon: Constants.DEFAULT_AVATAR_ICON_SVG,
            invite,
          };
          if (invite.toUser) {
            athlete = {
              isInvitation: true,
              name: `${invite.toUser.firstName} ${invite.toUser.lastName}`,
              email: invite.toUser.email,
              status: 'accepted',
              icon: invite.toUser?.image ? invite.toUser?.image.url : Constants.DEFAULT_AVATAR_ICON_SVG,
              invite,
            };
          }

          list.push(athlete);
          return list;
        }, []);

      if (!athletes || !athletes.length) {
        this.showAddAthlete = true;
      }
      this.existingAthletes = athletes;
    });
  }
}
